/*
  Gaia Iluka — Heritage Timeline: the thread
  File: assets/gi-heritage-thread.js

  Draws the string that runs pin to pin across the family archive
  (sections/gi-heritage-timeline.liquid).

  WHY THIS IS MEASURED RATHER THAN AUTHORED: the design export this
  section comes from carries one hand-drawn bezier written against a
  fixed 1920x1080 canvas. Here the milestones are editor blocks, so
  their number, their widths and their alternating heights are all
  unknown until the page lays out — and change again on resize, or the
  moment someone drags a block into a new position. So the path is
  built from where the pins actually ARE: read their centres, run a
  curve through them, write it into the <path>.

  CATMULL-ROM, CONVERTED TO CUBIC BEZIER: the curve has to pass exactly
  through every pin, or the string visibly misses the tack. That rules
  out plain quadratic smoothing, which only approaches its control
  points. Catmull-Rom interpolates its points by definition, and each
  segment converts to the cubic form SVG actually speaks with a fixed
  bit of arithmetic. Tension is deliberately loose (the /6 divisor is
  the standard uniform form) so the line hangs between pins rather than
  snapping taut between them.

  Runs in both orientations without knowing which it is in: the pins
  sit in a row on desktop and a column on mobile, and reading their
  live positions covers both. A ResizeObserver on the track catches the
  breakpoint change, images loading, and fonts settling.
*/
(function () {
  'use strict';

  var TRACK_SELECTOR = '[data-gi-heritage-track]';
  var THREAD_SELECTOR = '[data-gi-heritage-thread]';
  var PIN_SELECTOR = '.gi-her__pin';

  var instances = typeof WeakMap === 'function' ? new WeakMap() : null;

  function pinCentres(track) {
    var pins = track.querySelectorAll(PIN_SELECTOR);
    var base = track.getBoundingClientRect();
    var points = [];
    for (var i = 0; i < pins.length; i++) {
      var r = pins[i].getBoundingClientRect();
      if (!r.width && !r.height) continue;
      points.push({
        x: r.left - base.left + r.width / 2,
        y: r.top - base.top + r.height / 2
      });
    }
    return points;
  }

  function pathThrough(points) {
    if (points.length < 2) return '';
    var d = 'M' + points[0].x.toFixed(1) + ',' + points[0].y.toFixed(1);
    for (var i = 0; i < points.length - 1; i++) {
      var p0 = points[i - 1] || points[i];
      var p1 = points[i];
      var p2 = points[i + 1];
      var p3 = points[i + 2] || p2;
      var c1x = p1.x + (p2.x - p0.x) / 6;
      var c1y = p1.y + (p2.y - p0.y) / 6;
      var c2x = p2.x - (p3.x - p1.x) / 6;
      var c2y = p2.y - (p3.y - p1.y) / 6;
      d += 'C' + c1x.toFixed(1) + ',' + c1y.toFixed(1) +
           ' ' + c2x.toFixed(1) + ',' + c2y.toFixed(1) +
           ' ' + p2.x.toFixed(1) + ',' + p2.y.toFixed(1);
    }
    return d;
  }

  function createController(track) {
    var svg = track.querySelector(THREAD_SELECTOR);
    var path = svg && svg.querySelector('path');
    if (!path) return null;

    var frame = null;

    function draw() {
      frame = null;
      path.setAttribute('d', pathThrough(pinCentres(track)));
    }

    function schedule() {
      if (frame) return;
      frame = window.requestAnimationFrame(draw);
    }

    var ro = null;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(schedule);
      ro.observe(track);
    } else {
      window.addEventListener('resize', schedule, { passive: true });
    }

    /* Images arriving change the height of a milestone and therefore
       where its neighbours' pins sit. */
    var imgs = track.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) imgs[i].addEventListener('load', schedule, { once: true });
    }
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(schedule);
    }

    draw();

    return {
      destroy: function () {
        if (frame) window.cancelAnimationFrame(frame);
        if (ro) ro.disconnect();
        else window.removeEventListener('resize', schedule);
      }
    };
  }

  function init(track) {
    if (!track || !instances || instances.has(track)) return;
    var controller = createController(track);
    if (controller) instances.set(track, controller);
  }

  function destroy(track) {
    if (!track || !instances) return;
    var controller = instances.get(track);
    if (!controller) return;
    controller.destroy();
    instances.delete(track);
  }

  function initWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var found = scope.matches && scope.matches(TRACK_SELECTOR)
      ? [scope]
      : scope.querySelectorAll(TRACK_SELECTOR);
    found.forEach ? found.forEach(init) : Array.prototype.forEach.call(found, init);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    initWithin(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    initWithin(evt.target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var track = evt.target && evt.target.querySelector
      ? evt.target.querySelector(TRACK_SELECTOR)
      : null;
    if (track) destroy(track);
  });
})();
